/**
 * xterm.js log viewer (§4.4.5).
 *
 * React NEVER owns log DOM — we hand a host <div> to xterm and only
 * control it via its API. The toolbar (search, pause, download) lives
 * outside the terminal frame.
 *
 * Back-pressure: bounded scrollback (5000 lines) is enforced by xterm.
 * If we overflow we surface a dropped-marker line manually.
 *
 * Polling mode: `onFetchLogs` returns the last N tail lines. We diff
 * against the last rendered batch to avoid re-printing old lines on
 * every poll. Not perfect — file rotation drops us back to full-reprint
 * on next poll — but good enough for v1.
 */
import { useEffect, useRef, useState } from 'react';
import {
  Download,
  Search as SearchIcon,
  Pause,
  Play,
  RotateCw,
  Wifi,
  WifiOff,
} from 'lucide-react';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { SearchAddon } from '@xterm/addon-search';
import '@xterm/xterm/css/xterm.css';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

export interface LogTailData {
  lines: string[];
}

export interface LogViewerProps {
  onFetchLogs: (tail?: number) => Promise<LogTailData | null>;
  refreshInterval?: number;
  downloadUrl?: string;
  className?: string;
  /** Optional label announced by aria-live when status changes. */
  agentName?: string;
}

export function LogViewer({
  onFetchLogs,
  refreshInterval = 2_000,
  downloadUrl,
  className,
  agentName,
}: LogViewerProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const termRef = useRef<Terminal | null>(null);
  const fitRef = useRef<FitAddon | null>(null);
  const searchRef = useRef<SearchAddon | null>(null);
  const lastLinesRef = useRef<string[]>([]);

  const [paused, setPaused] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<'connecting' | 'connected' | 'reconnecting' | 'paused'>(
    'connecting'
  );

  // Mount the terminal once.
  useEffect(() => {
    if (!hostRef.current) return;
    const term = new Terminal({
      convertEol: true,
      scrollback: 5000,
      fontFamily: "'JetBrains Mono', monospace",
      fontSize: 12,
      lineHeight: 1.3,
      theme: {
        background: '#0b0b0f',
        foreground: '#e4e4e7',
        cursor: '#e4e4e7',
      },
    });
    const fit = new FitAddon();
    const search = new SearchAddon();
    term.loadAddon(fit);
    term.loadAddon(search);
    term.open(hostRef.current);
    try {
      fit.fit();
    } catch {
      /* host not sized yet */
    }

    termRef.current = term;
    fitRef.current = fit;
    searchRef.current = search;

    const onResize = () => {
      try {
        fit.fit();
      } catch {
        /* ignore */
      }
    };
    window.addEventListener('resize', onResize);

    // Use a ResizeObserver on the host so tab switches resize correctly.
    const ro = new ResizeObserver(onResize);
    ro.observe(hostRef.current);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', onResize);
      term.dispose();
      termRef.current = null;
      fitRef.current = null;
      searchRef.current = null;
    };
  }, []);

  // Polling loop.
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const append = (lines: string[]) => {
      const term = termRef.current;
      if (!term) return;

      // Naive diff: strip the prefix we already wrote.
      const prev = lastLinesRef.current;
      let startIdx = 0;
      if (prev.length && lines.length) {
        // Anchor on last previous line; if we find it, everything after
        // is new. If not, assume rotation — reprint everything.
        const anchor = prev[prev.length - 1];
        const found = lines.lastIndexOf(anchor);
        if (found >= 0) {
          startIdx = found + 1;
        } else {
          term.clear();
          startIdx = 0;
        }
      } else if (!prev.length) {
        // First render — just dump everything.
        startIdx = 0;
      }

      for (let i = startIdx; i < lines.length; i++) {
        term.writeln(lines[i]);
      }
      lastLinesRef.current = lines;
    };

    const tick = async () => {
      if (cancelled) return;
      if (paused) {
        setStatus('paused');
        timer = setTimeout(tick, refreshInterval);
        return;
      }
      try {
        const data = await onFetchLogs(1000);
        if (cancelled) return;
        if (data) {
          append(data.lines);
          setStatus('connected');
        }
      } catch {
        if (cancelled) return;
        setStatus('reconnecting');
      }
      if (!cancelled) {
        timer = setTimeout(tick, refreshInterval);
      }
    };

    tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [onFetchLogs, refreshInterval, paused]);

  function handleSearch(dir: 'next' | 'prev') {
    const search = searchRef.current;
    if (!search || !query) return;
    if (dir === 'next') search.findNext(query);
    else search.findPrevious(query);
  }

  function handleClearDisplay() {
    termRef.current?.clear();
    lastLinesRef.current = [];
  }

  return (
    <div className={cn('flex flex-col h-full', className)}>
      <div className="flex items-center gap-2 p-2 border-b bg-muted/30">
        <span
          className="flex items-center gap-1 text-xs text-muted-foreground"
          aria-live="polite"
          aria-label={agentName ? `Logs for ${agentName}` : undefined}
        >
          {status === 'connected' ? (
            <Wifi className="h-3.5 w-3.5 text-success" />
          ) : status === 'paused' ? (
            <Pause className="h-3.5 w-3.5" />
          ) : (
            <WifiOff className="h-3.5 w-3.5 text-warning" />
          )}
          {status}
        </span>
        <div className="flex-1" />
        {searchOpen && (
          <div className="flex items-center gap-1">
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch(e.shiftKey ? 'prev' : 'next');
                }
                if (e.key === 'Escape') {
                  setSearchOpen(false);
                  setQuery('');
                }
              }}
              placeholder="Search"
              className="h-7 w-48 text-xs"
              aria-label="Search logs"
            />
            <Button size="sm" variant="ghost" onClick={() => handleSearch('prev')}>
              Prev
            </Button>
            <Button size="sm" variant="ghost" onClick={() => handleSearch('next')}>
              Next
            </Button>
          </div>
        )}
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setSearchOpen((v) => !v)}
          aria-label="Toggle search"
        >
          <SearchIcon className="h-4 w-4" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setPaused((v) => !v)}
          aria-label={paused ? 'Resume' : 'Pause'}
        >
          {paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={handleClearDisplay}
          aria-label="Clear display"
          title="Clear display (does not truncate the log file)"
        >
          <RotateCw className="h-4 w-4" />
        </Button>
        {downloadUrl && (
          <Button size="sm" variant="ghost" asChild>
            <a href={downloadUrl} download aria-label="Download logs">
              <Download className="h-4 w-4" />
            </a>
          </Button>
        )}
      </div>
      <div ref={hostRef} className="flex-1 min-h-0 bg-black" />
    </div>
  );
}
